import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastService } from '@/app/services/toast.service';
import { catchError, tap, throwError } from 'rxjs';
import { ERROR_MESSAGE, ErrorMessageType, SHOULD_SHOW_ERROR_TOAST, SHOULD_SHOW_SUCCESS_TOAST, SUCCESS_MESSAGE } from '../services/base.service';
import { LanguageService } from '../services/language.service';

export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const languageService = inject(LanguageService);
  const url = req.url;
  const method = req.method.toUpperCase();

  // totally exclude these endpoints from showing toasts
 const NO_SHOW_TOAST_RULES = [
    { pattern: '/api/v1/SystemOwner/list', methods: ['POST'] },
    { pattern: '/api/v1/integrations/DcodeIntegration/families/compact', methods: ['GET'] },
    { pattern: '/api/v1/SoftwareCenter/list', methods: ['POST'] },
    // SoftwareCenter?id= only excluets GET requests
    { pattern: '/api/v1/SoftwareCenter?id=', methods: ['GET'] },
    // translations loaded by TranslateHttpLoader (prefix could be './assets/i18n/' or 'assets/i18n/')
    { pattern: 'assets/i18n/', methods: ['*'] },
  ];

  const isExcluded = NO_SHOW_TOAST_RULES.some(rule => {
    const matchesUrl = url.includes(rule.pattern);
    const matchesMethod = rule.methods.includes('*') || rule.methods.includes(method);
    return matchesUrl && matchesMethod;
  });

  const shouldShowToastFromHardRules = !isExcluded;

  return next(req).pipe(
    tap((response) => {
      // (status 200-299)
      const shouldShowSuccessToastFromCustom = req.context.get(SHOULD_SHOW_SUCCESS_TOAST);
      if(shouldShowSuccessToastFromCustom === false) {
        // do nothing
      } else if ((shouldShowToastFromHardRules) && response instanceof HttpResponse && response.ok) {
        const customSuccessMessage = req.context.get(SUCCESS_MESSAGE);
        let successMessage = languageService.instant('toast.requestSuccessful');
        switch (method) {
          case 'POST':
            successMessage = customSuccessMessage || languageService.instant('toast.requestSuccessful');
            break;
          case 'PUT':
            successMessage = languageService.instant('toast.updatedSuccessfully');
            break;
          case 'DELETE':
            successMessage = languageService.instant('toast.deletedSuccessfully');
            break;
          case 'PATCH':
            successMessage = languageService.instant('toast.modifiedSuccessfully');
            break;
        }
        toastService.showSuccess(successMessage);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      const shouldShowErrorToastFromCustom = req.context.get(SHOULD_SHOW_ERROR_TOAST);
      if(shouldShowErrorToastFromCustom === false) {
        // do nothing
      } else {
        const customErrorMessage = req.context.get(ERROR_MESSAGE);
        let errorMessageByType = new Map<number | string, string>();
        customErrorMessage?.forEach((item: ErrorMessageType) => {
          errorMessageByType.set(item.type, item.message);
        });
        function getErrorMessages(statusCode: number | string) {
          return errorMessageByType.get(statusCode) || errorMessageByType.get('default');
        }
        switch (error.status) {
            case 400:
              if(error.error.status==400 && Array.isArray(error.error.errors) && error.error.errors.length>0){
                return throwError(() => error);
              }else{
                toastService.showError( languageService.instant('toast.error'), languageService.instant('toast.error400'), error.error.message || '');
              }
              
            break;
          case 401:
            // toastService.showError( 'Error', 'Unauthorized','Authentication failed, please login again');
            break;
          case 403:
            toastService.showError(
              languageService.instant('toast.error'),
              languageService.instant('toast.error403'),
              languageService.instant('toast.error403Message'),
            );
            break;
          case 404:
            toastService.showError(
              languageService.instant('toast.error'),
              languageService.instant('toast.error404'),
              languageService.instant('toast.error404Message'),
            );
            break;
          case 500:
            toastService.showError(
              languageService.instant('toast.error'),
              languageService.instant('toast.error500'),
              languageService.instant('toast.error500Message'),
            );
            break;
          case 0:
              toastService.showError(
                languageService.instant('toast.error'),
                `${error.status} ${languageService.instant('toast.error')}`,
                languageService.instant('toast.requestFailed', { message: error.message || 'Unknown error' })
              );
            break;
  
          default:
            if (error.status >= 400) {
              toastService.showError(
                languageService.instant('toast.error'),
                `${error.status} ${languageService.instant('toast.error')}`,
                languageService.instant('toast.requestFailed', { message: error.message || 'Unknown error' })
              );
            }
        }
      }

      return throwError(() => error);
    })
  );
};
